import type { RevampCategoryChip } from '../type';

/** The nav tree shape `getArticleNavigationItems` returns. */
export interface CategoryNavItem {
    /** CMS category ID. Absent on the synthetic root entry the nav prepends. */
    id?: number;
    link: string;
    title: string;
    children?: CategoryNavItem[];
}

export interface CategoryRail {
    /** The category being browsed, for the masthead. Not a rail position. */
    active?: RevampCategoryChip;
    /** Every category, in nav order. The same list on every page. */
    items: RevampCategoryChip[];
}

const stripTrailingSlash = (path: string): string =>
    path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

const toChip = (item: CategoryNavItem, index: number): RevampCategoryChip => ({
    // The CMS ID where the nav carries one; links are unique within the tree,
    // so the index is a serviceable stand-in for the entries that do not.
    id: item.id ?? index,
    title: item.title,
    link: item.link,
});

/**
 * The category rail's contents.
 *
 * The rail is the same list in the same order on every page — browsing into a
 * category marks its pill, it does not move it. Anything that reorders on
 * navigation makes the row jump under the reader's cursor and loses the
 * position they were scanning.
 *
 * `active` is returned for the masthead only. The pill's active state comes
 * from the pathname via NavLink, which also covers sub-categories: on
 * `/articles/design/kitchens` the `Design` pill is still the one lit.
 */
export const deriveCategoryRail = (
    navItems: CategoryNavItem[],
    pathname: string,
): CategoryRail => {
    const path = stripTrailingSlash(pathname);

    // The synthetic `All` entry the nav prepends is not a category.
    const roots = navItems.filter(
        (item) => stripTrailingSlash(item.link) !== '/articles',
    );

    const items = roots.map(toChip);

    const activeRootIndex = roots.findIndex(
        (item) => stripTrailingSlash(item.link) === path,
    );
    if (activeRootIndex !== -1) {
        return { active: items[activeRootIndex], items };
    }

    // A sub-category: name it in the masthead, but keep the top-level rail.
    for (const root of roots) {
        const child = (root.children ?? []).find(
            (item) => stripTrailingSlash(item.link) === path,
        );
        if (child) {
            return { active: toChip(child, -1), items };
        }
    }

    return { items };
};
